import { SpecialTileTypes } from "./TileTypes";
import { Settings } from '../../Settings';
import Vector from "../math/Vector";
import GridBox from "./GridBox";
import GridRegion from "./GridRegion";
import WorldGrid from './WorldGrid';
import Sprite from "../sprite/Sprite"
import Grid from "./Grid";
import TileType from "./TileType";
import TileTemplate from './TileTemplate';
import TileLighting from '../lighting/TileLighting';
import PathData from './PathData';
import { TileTypeRarity } from './TileTypeRarity';
import BigNum from '../math/BigNum';
import FixtureDescription from '../fixture/FixtureDescription';
import Fast from '../../util/Fast';
import Color from "../color/Color";

const NEIGHBOR_TOP_INDEX = 0;
const NEIGHBOR_BOTTOM_INDEX = 1;
const NEIGHBOR_RIGHT_INDEX = 2;
const NEIGHBOR_LEFT_INDEX = 3;

const WORKING_PERCENT = new BigNum(0);

export default class GridTile extends GridBox {
    /**
     * @param {Grid} grid
     * @param {GridRegion} region 
     * @param {Vector} origin 
     */
    constructor(grid, region, origin) {
        super(origin);

        /**
         * @constant
         * @type {Grid}
         */
        this.grid = grid;

        /**
         * @constant
         * @type {GridRegion}
         */
        this.region = region;

        /**
         * @type {Sprite}
         */
        this.sprite = null;
        /**
         * @type {Sprite}
         */
        this.backgroundSprite = null;

        /**
         * Every tile knows its 4 neighbors. This is used for faster path finding.
         * @constant
         * @type {Array.<GridTile>}
         */
        this.neighbors = new Array(4);
        this.neighbors[NEIGHBOR_TOP_INDEX] = null;      // Top index
        this.neighbors[NEIGHBOR_BOTTOM_INDEX] = null;   // Bottom index
        this.neighbors[NEIGHBOR_RIGHT_INDEX] = null;    // Right index
        this.neighbors[NEIGHBOR_LEFT_INDEX] = null;     // Left index

        /**
         * @type {TileType}
         */
        this.tileType = SpecialTileTypes.EMPTY;
        /**
         * This must always be set when the tile type is set.
         * @type {TileTemplate}
         */
        this.tileTemplate = null;

        /**
         * @type {number}
         */
        this.borderCode = 0;

        /**
         * True if the tile has been mined/cleared.
         * @type {boolean}
         */
        this.open = false;

        /**
         * @type {FixtureDescription}
         */
        this.fixtureDescription = null;

        /**
         * @constant
         * @type {TileLighting}
         */
        this.lighting = new TileLighting();

        /**
         * @constant
         * @type {PathData}
         */
        this.pathData = new PathData();

        /////////////////////////////////////////
        // Health
        /////////////////////////////////////////

        /**
         * @type {BigNum}
         */
        this.initialHealth = null;

        /**
         * @type {BigNum}
         */
        this.health = null;

        /**
         * @type {number}
         */
        this.healthPercent = 100;

        /**
         * @type {number}
         */
        this.mineTileIndex = -1;
    }

    /**
     * Invoked when assigned to a grid.
     * @param {number} originX 
     * @param {number} originY 
     */
    initialize(originX, originY) {

        this.origin.set(originX, originY);

        this.tileType = SpecialTileTypes.EMPTY;
        this.tileTemplate = null;
        this.sprite = null;
        this.fixtureDescription = null;
        this.backgroundSprite = null;
        this.open = false;
        this.mineTileIndex = -1;

        this.health = null;
        this.initialHealth = null;
        this.healthPercent = 100;

        this.lighting.reset();
    }

    /**
     * Invoked when the terrain generator has assigned a tile type and the open/closed status of the tile.
     * @param {boolean} open 
     * @param {TileType} tileType 
     */
    tileInit(open, tileType) {
        this.borderCode = -1;
        this.open = open;
        this.setTileType(tileType);
    }

    /**
     * @param {TileType} tileType 
     */
    setTileType(tileType) {
        if (!tileType) {
            console.log('GridTyple.setTileType is null');
            return;
        }

        if (this.tileType === tileType) {
            return;
        }

        this.tileType = tileType;
        this.tileTemplate = tileType.getRandomTemplate();
        if (this.tileTemplate && !this.open && this.borderCode != -1) {
            this.sprite = this.tileTemplate.getTileSprite(this.borderCode);
        }
    }

    /**
     * @returns {BigNum}
     */
    getHealth() {
        if (this.open) {
            return null;
        }

        if (!this.health) {
            this.calculateTileHealth();
        }

        return this.health;
    }

    /**
     * @param {BigNum} goldOutput
     */
    calculateTileGold(goldOutput) {

        if (this.tileType.rarityModifier !== TileTypeRarity.GOLD) {
            goldOutput.setZero();
            return;
        }

        // Points do not scale with tile row like ore does.
        goldOutput.copy(this.tileType.layerDescription.baseGold);
    }

    /**
     * @private
     */
    calculateTileHealth() {
        if (this.open) {
            this.health = null;
        }
        else {

            this.health = new BigNum(0);

            this.tileType.layerDescription.calculateTileHealth(this.getTileRow(), this.health);

            this.initialHealth = new BigNum(0);
            this.initialHealth.copy(this.health);
        }
    }


    /**
     * @returns {boolean}
     */
    isCommon() {
        return this.tileType.rarityModifier === TileTypeRarity.COMMON;
    }

    /**
     * @returns {boolean}
     */
    isCurrency() {
        return this.tileType.rarityModifier === TileTypeRarity.ORE;
    }

    /**
     * @returns {boolean}
     */
    isPriority() {
        return this.tileType.rarityModifier === TileTypeRarity.GOLD
            || this.tileType.rarityModifier === TileTypeRarity.RUBY
            || this.tileType.rarityModifier === TileTypeRarity.UPGRADE;
    }


    /**
     * @returns {Grid}
     */
    getGrid() {
        return this.grid;
    }

    /**
     * @returns {GridRegion}
     */
    getRegion() {
        return this.region;
    }

    /**
     * @returns {WorldGrid}
     */
    getWorldGrid() {
        return this.grid.getWorldGrid();
    }

    /**
     * @returns {Sprite}
     */
    getSprite() {
        return this.sprite;
    }

    /**
     * @param {Sprite} sprite 
     */
    setSprite(sprite) {
        this.sprite = sprite;
    }

    /**
     * @returns {Vector}
     */
    createCenterPositionVector() {
        const position = new Vector(0, 0);
        this.assignCenterPosition(position);
        return position;
    }

    /**
     * @param {Vector} position 
     */
    assignCenterPosition(position) {
        position.set(this.origin.x + Settings.tile.halfSize, this.origin.y + Settings.tile.halfSize);
    }

    /**
     * @returns {boolean}
     */
    isTraversable() {
        if (this.open) {
            return !this.fixtureDescription || this.fixtureDescription.traversable;
        }
        else {
            return false;
        }
    }

    /**
     * @returns {boolean}
     */
    isOpaque() {
        if (this.open) {
            return this.fixtureDescription && this.fixtureDescription.opaque;
        }
        else {
            return this.tileType.opaque;
        }
    }

    /**
     * @returns {number}
     */
    getPixelWidth() {
        return Settings.tile.size;
    }

    /**
     * @returns {number}
     */
    getPixelHeight() {
        return Settings.tile.size;
    }

    /**
     * @returns {number}
     */
    getTileCol() {
        return this.getOriginTileCol();
    }

    /**
     * @returns {number}
     */
    getTileRow() {
        return this.getOriginTileRow();
    }

    /**
     * @returns {string}
     */
    createId() {
        return this.getTileCol() + "_" + this.getTileRow();
    }

    /**
     * @returns {Array.<GridTile>}
     */
    getNeighbors() {
        return this.neighbors;
    }

    findNeighbors() {
        this.neighbors[0] = null;
        this.neighbors[1] = null;
        this.neighbors[2] = null;
        this.neighbors[3] = null;

        const col = this.getTileCol();
        const row = this.getTileRow();

        const topNeighbor = this.getNeighborColRow(col, row - 1); // -1 row is north
        if (topNeighbor) {
            this.neighbors[NEIGHBOR_TOP_INDEX] = topNeighbor;

            // Make sure that this tile is the bottom neighbor of topNeighbor.
            topNeighbor.neighbors[NEIGHBOR_BOTTOM_INDEX] = this;
        }
        const bottomNeighbor = this.getNeighborColRow(col, row + 1); // +1 row is south
        if (bottomNeighbor) {
            this.neighbors[NEIGHBOR_BOTTOM_INDEX] = bottomNeighbor;

            // Make sure that this tile is the top neighbor of bottomNeighbor.
            bottomNeighbor.neighbors[NEIGHBOR_TOP_INDEX] = this;
        }
        const rightNeighbor = this.getNeighborColRow(col + 1, row);
        if (rightNeighbor) {
            this.neighbors[NEIGHBOR_RIGHT_INDEX] = rightNeighbor;

            // Make sure that this tile is the left neighbor of rightNeighbor.
            rightNeighbor.neighbors[NEIGHBOR_LEFT_INDEX] = this;
        }
        const leftNeighbor = this.getNeighborColRow(col - 1, row);
        if (leftNeighbor) {
            this.neighbors[NEIGHBOR_LEFT_INDEX] = leftNeighbor;

            // Make sure that this tile is the right neighbor of leftNeighbor.
            leftNeighbor.neighbors[NEIGHBOR_RIGHT_INDEX] = this;
        }
    }

    nullifyNeighborRight() {
        this.neighbors[NEIGHBOR_RIGHT_INDEX] = null;
    }
    nullifyNeighborLeft() {
        this.neighbors[NEIGHBOR_LEFT_INDEX] = null;
    }
    nullifyNeighborBottom() {
        this.neighbors[NEIGHBOR_BOTTOM_INDEX] = null;
    }
    nullifyNeighborTop() {
        this.neighbors[NEIGHBOR_TOP_INDEX] = null;
    }

    findNeighborRight() {
        this.neighbors[NEIGHBOR_RIGHT_INDEX] = null;

        const col = this.getTileCol();
        const row = this.getTileRow();

        const rightNeighbor = this.getNeighborColRow(col + 1, row);
        if (rightNeighbor) {
            this.neighbors[NEIGHBOR_RIGHT_INDEX] = rightNeighbor;

            // Make sure that this tile is the left neighbor of rightNeighbor.
            rightNeighbor.neighbors[NEIGHBOR_LEFT_INDEX] = this;
        }
    }

    findNeighborLeft() {
        this.neighbors[NEIGHBOR_LEFT_INDEX] = null;

        const col = this.getTileCol();
        const row = this.getTileRow();

        const leftNeighbor = this.getNeighborColRow(col - 1, row);
        if (leftNeighbor) {
            this.neighbors[NEIGHBOR_LEFT_INDEX] = leftNeighbor;

            // Make sure that this tile is the right neighbor of leftNeighbor.
            leftNeighbor.neighbors[NEIGHBOR_RIGHT_INDEX] = this;
        }
    }
    
    findNeighborTop() {
        this.neighbors[NEIGHBOR_TOP_INDEX] = null;

        const col = this.getTileCol();
        const row = this.getTileRow();

        const topNeighbor = this.getNeighborColRow(col, row - 1);
        if (topNeighbor) {
            this.neighbors[NEIGHBOR_TOP_INDEX] = topNeighbor;

            // Make sure that this tile is the bottom neighbor of topNeighbor.
            topNeighbor.neighbors[NEIGHBOR_BOTTOM_INDEX] = this;
        }
    }

    findNeighborBottom() {
        this.neighbors[NEIGHBOR_BOTTOM_INDEX] = null;

        const col = this.getTileCol();
        const row = this.getTileRow();

        const bottomNeighbor = this.getNeighborColRow(col, row + 1);
        if (bottomNeighbor) {
            this.neighbors[NEIGHBOR_BOTTOM_INDEX] = bottomNeighbor;

            // Make sure that this tile is the top neighbor of bottomNeighbor.
            bottomNeighbor.neighbors[NEIGHBOR_TOP_INDEX] = this;
        }
    }

    /**
     * @param {GridTile} tile 
     * @returns {boolean}
     */
    isAdjacentTo(tile) {
        const row1 = this.getTileRow();
        const row2 = tile.getTileRow();

        if (Math.abs(row1 - row2) > 1) {
            return false;
        }

        const col1 = this.getTileCol();
        const col2 = tile.getTileCol();

        // Common simple case:
        if (Math.abs(col1 - col2) > 1) {
            return false;
        }

        return true;
    }

    /**
     * @param {GridTile} tile 
     * @returns {boolean}
     */
    isNeighborTile(tile) {
        return tile === this.neighbors[0]
            || tile === this.neighbors[1]
            || tile === this.neighbors[2]
            || tile === this.neighbors[3];
    }

    /**
     * @private
     * @param {number} col 
     * @param {number} row 
     * @returns {GridTile}
     */
    getNeighborColRow(col, row) {
        // Do a quick lookup within the region. If it is not found, then
        // lets just ask the world grid for the tile.
        let neighbor = this.region.getGridTileAbsolute(col, row);
        if (!neighbor) {
            const zone = this.region.zone;
            neighbor = zone.getGridTileAbsolute(col, row);

            if (!neighbor) {
                const grid = zone.grid;
                neighbor = grid.getGridTileAbsolute(col, row);

                // If we still don't have the neighbor we can try using the world grid.        
                if (!neighbor) {
                    const worldGrid = zone.grid.worldGrid;
                    neighbor = worldGrid.getGridTile(col, row);
                }
            }
        }

        return neighbor;
    }

    /**
     * @param {Vector} position
     * @returns {GridTile}
     */
    getNeighbor(position) {
        // If this tile contains the position, then we are done.
        if (this.contains(position)) {
            return this;
        }

        const neighbor = this.findNeighborContaining(position);
        if (neighbor) {
            return neighbor;
        }

        // final int px = (int) position.x;
        // final int py = (int) position.y;

        // final int tileCol = px / Settings.tile.size;
        // final int tileRow = py / Settings.tile.size;

        const tileCol = Fast.floor(position.x / Settings.tile.size);
        const tileRow = Fast.floor(position.y / Settings.tile.size);

        // This can return null if the col/row is in a grid that hasn't been loaded yet. The zones are null.
        return this.getNeighborColRow(tileCol, tileRow);
    }

    /**
     * @returns {GridTile}
     */
    getNeighborLeft() {
        return this.neighbors[NEIGHBOR_LEFT_INDEX];
    }
    /**
     * @returns {GridTile}
     */
    getNeighborRight() {
        return this.neighbors[NEIGHBOR_RIGHT_INDEX];
    }
    /**
     * @returns {GridTile}
     */
    getNeighborTop() {
        return this.neighbors[NEIGHBOR_TOP_INDEX];
    }
    /**
     * @returns {GridTile}
     */
    getNeighborBottom() {
        return this.neighbors[NEIGHBOR_BOTTOM_INDEX];
    }

    /**
     * @returns {GridTile}
     */
    getNeighborTopLeft() {
        const top = this.neighbors[NEIGHBOR_TOP_INDEX];
        if (!top) {
            return null;
        }

        return top.getNeighborLeft();
    }
    /**
     * @returns {GridTile}
     */
    getNeighborTopRight() {
        const top = this.neighbors[NEIGHBOR_TOP_INDEX];
        if (!top) {
            return null;
        }

        return top.getNeighborRight();
    }
    /**
     * @returns {GridTile}
     */
    getNeighborBottomLeft() {
        const bottom = this.neighbors[NEIGHBOR_BOTTOM_INDEX];
        if (!bottom) {
            return null;
        }

        return bottom.getNeighborLeft();
    }
    /**
     * @returns {GridTile}
     */
    getNeighborBottomRight() {
        const bottom = this.neighbors[NEIGHBOR_BOTTOM_INDEX];
        if (!bottom) {
            return null;
        }

        return bottom.getNeighborRight();
    }

    /**
     * @param {GridTile} tile
     * @returns {boolean}
     */
    isNeighborLeft(tile) {
        return tile === this.neighbors[NEIGHBOR_LEFT_INDEX];
    }
    /**
     * @param {GridTile} tile
     * @returns {boolean}
     */
    isNeighborRight(tile) {
        return tile === this.neighbors[NEIGHBOR_RIGHT_INDEX];
    }
    /**
     * @param {GridTile} tile
     * @returns {boolean}
     */
    isNeighborTop(tile) {
        return tile === this.neighbors[NEIGHBOR_TOP_INDEX];
    }
    /**
     * @param {GridTile} tile
     * @returns {boolean}
     */
    isNeighborBottom(tile) {
        return tile === this.neighbors[NEIGHBOR_BOTTOM_INDEX];
    }

    /**
     * @param {Vector} coordinate 
     * @returns {GridTile}
     */
    findNeighborContaining(coordinate) {
        for (let i = 0; i < this.neighbors.length; i++) {
            const neighbor = this.neighbors[i];
            if (neighbor && neighbor.contains(coordinate)) {
                return neighbor;
            }
        }

        const topLeft = this.getNeighborTopLeft();
        if (topLeft && topLeft.contains(coordinate)) {
            return topLeft;
        }

        const topRight = this.getNeighborTopRight();
        if (topRight && topRight.contains(coordinate)) {
            return topRight;
        }

        const bottomLeft = this.getNeighborBottomLeft();
        if (bottomLeft && bottomLeft.contains(coordinate)) {
            return bottomLeft;
        }

        const bottomRight = this.getNeighborBottomRight();
        if (bottomRight && bottomRight.contains(coordinate)) {
            return bottomRight;
        }

        return null;
    }

    /**
     * @param {number} tileColDelta 
     * @param {number} tileRowDelta 
     * @returns {GridTile}
     */
    findRelativeTile(tileColDelta, tileRowDelta) {
        const worldGrid = this.getGrid().getWorldGrid();

        const tileCol = this.getTileCol() + tileColDelta;
        const tileRow = this.getTileRow() + tileRowDelta;

        return worldGrid.getGridTile(tileCol, tileRow);
    }

    evaluateBorders() {
        // If this is an open tile, then we just need to make sure that we are using the open sprite.
        if (this.open) {
            this.borderCode = 0;
            this.sprite = null;
        }
        else {
            const prevBorderCode = this.borderCode,
                topNeighbor = this.neighbors[NEIGHBOR_TOP_INDEX],
                bottomNeighbor = this.neighbors[NEIGHBOR_BOTTOM_INDEX], 
                rightNeighbor = this.neighbors[NEIGHBOR_RIGHT_INDEX],
                leftNeighbor = this.neighbors[NEIGHBOR_LEFT_INDEX];

            const n = (topNeighbor && topNeighbor.open) ? 1 : 0,
                s = (bottomNeighbor && bottomNeighbor.open) ? 2 : 0,
                w = (leftNeighbor && leftNeighbor.open) ? 4 : 0,
                e = (rightNeighbor && rightNeighbor.open) ? 8 : 0;
                
            this.borderCode = n + s + w + e;

            // border code of 0 means the tile has no open neighbors.
            // 15 = fully exposed to open air.
            // 0 = all 4 neighbors are closed.
            // See TileBorderCode.

            // Only update the sprite if the border code changes.
            if (prevBorderCode !== this.borderCode && this.tileTemplate) {
                this.sprite = this.tileTemplate.getTileSprite(this.borderCode);
            }
        }
    }

    // /**
    //  * Invoked on an open tile that has been made closed.
    //  */
    // onTileFilled() {
    //     // Change the state to closed
    //     this.open = false;

    //     // Force the border code to be calculated.
    //     this.borderCode = -1;

    //     // Update the sprite.
    //     this.evaluateBorders();

    //     // Update the state change and add the tile to the grid state change collection.
    //     this.handleTileClosed();

    //     this.health = null;        

    //     // Mark the grid state as changed.
    //     this.getGrid().markGridAsChanged();

    //     //////this.calculateTileHealth();
        
    //     // Update the neighbor tile borders
    //     for (let i = 0; i < this.neighbors.length; i++) {
    //         const neighbor = this.neighbors[i];
    //         if (neighbor) {
    //             neighbor.evaluateBorders();
    //         }
    //     }
    // }

    /**
     * Invoked when a character has mined the tile.
     * @param {BigNum} damage The damage in the current frame
     */
    onTileDamaged(damage) {
        if (this.open || !damage) {
            return;
        }

        // See if we need to lazily calculate the health of the tile.
        if (!this.health) {
            this.calculateTileHealth();
        }

        this.health.sub(damage);
        if (this.health.lessThanEqualsZero()) {
            this.onTileBreak();
        }
        else {
            this.healthPercent = this.calculateHealthPercent();
        }
    }

    /**
     * @private
     * @returns {number}
     */
    calculateHealthPercent() {
        if (this.open) {
            return 0;
        }
        if (!this.health) {
            return 100;
        }
        if (this.health.equalsZero()) {
            return 0;
        }

        // Calculate the ratio of health to initial health.
        WORKING_PERCENT.copy(this.health);
        WORKING_PERCENT.divide(this.initialHealth);
        
        // The result of the division should be able to fit in a 32-bit number.

        const ratio = WORKING_PERCENT.toNumber();
        return (ratio * 100) | 0;  // Convert it to a percent.
    }

    /**
     * @returns {boolean}
     */
    isTileDamaged() {
        if (this.open) {
            return false;
        }
        if (!this.health) {
            return false;
        }

        return !this.health.equals(this.initialHealth);
    }

    /**
     * Invoked on the tile when it has been broken.
     */
    onTileBreak() {

        if (!this.open) {
            // Change the open state to true.
            this.open = true;
            
            // Force the border code to be calculated.
            this.borderCode = -1;

            // Update the sprite.
            this.evaluateBorders();

            // Update the state change and add the tile to the grid state change collection.
            this.handleTileOpened();

            // If the tile above this tile has a fixture, then we want to remove it, or else we'll have 
            // floating decorations.
            const top = this.getNeighborTop();
            if (top && top.open && top.fixtureDescription) {
                top.fixtureDescription = null;
            }

            const bottom = this.getNeighborBottom();
            if (bottom && bottom.open && bottom.fixtureDescription) {
                bottom.fixtureDescription = null;
            }

            // Clear out the mine score
            this.pathData.mineScore = 0;
            this.pathData.mineVectorFieldNumber = 0;

            // Reset health
            this.health = null;
            this.initialHealth = null;
            this.healthPercent = 0;

            //////////////////////////////////////////////
            if (this.mineTileIndex >= 0) {
                this.region.mineTiles[this.mineTileIndex] = null;
                this.mineTileIndex = -1;
            }
            //////////////////////////////////////////////

            // Mark the grid state as changed.
            this.getGrid().markGridAsChanged();
        }

        // Update the neighbor tile borders
        for (let i = 0; i < this.neighbors.length; i++) {
            const neighbor = this.neighbors[i];
            if (neighbor) {
                neighbor.evaluateBorders();
            }
        }
    }

    /**
     * @private
     */
    handleTileOpened() {
        // If the tile is actually not open, then don't do anything.
        if (!this.open) {
            return;
        }
        
        // If above tile is open and sunlit, then this tile is now sunlit
        const neighborTop = this.getNeighborTop();
        if (neighborTop && neighborTop.open && neighborTop.lighting.sunlightFull) {
            this.handleSunlightStart();
        }
    }

    /**
     */
    handleSunlightStart() {
        //////this.lighting.sunlightFull = true;
        this.markAsSunlightFull();

        /////////////////////////////////////////////////////////////
        // If the tile is open, then apply sunlight to neighbor tiles.
        // If the tile is closed, then don't propogate changes.
        /////////////////////////////////////////////////////////////

        // if (!this.open) {
        //     return;
        // }

        //////////////////////////////////////
        // Apply partial sunlight to side neighbors
        // Don't generate new state changes, just update the states.
        //////////////////////////////////////

        const neighborLeft = this.getNeighborLeft();
        if (neighborLeft && !neighborLeft.lighting.sunlightFull) {
            neighborLeft.lighting.sunlightPartialIncrement();
        }

        const neighborRight = this.getNeighborRight();
        if (neighborRight && !neighborRight.lighting.sunlightFull) {
            neighborRight.lighting.sunlightPartialIncrement();
        }

        //////////////////////////////////////
        // Apply full sunlight to bottom neighbor
        //////////////////////////////////////

        // If the tile is not open, then don't deal with the bottom neighbor
        if (!this.open) {
            return;
        }

        const neighborBottom = this.getNeighborBottom();
        if (neighborBottom && !neighborBottom.lighting.sunlightFull) {
            neighborBottom.handleSunlightStart();
        }
    }

    /**
     */
     handleSunlightStop() {
        this.lighting.sunlightFull = false;

        //////////////////////////////////////
        // Remove partial sunlight from side neighbors
        //////////////////////////////////////

        const neighborLeft = this.getNeighborLeft();
        if (neighborLeft) {
            neighborLeft.lighting.sunlightPartialDecrement();
        }

        const neighborRight = this.getNeighborRight();
        if (neighborRight) {
            neighborRight.lighting.sunlightPartialDecrement();
        }

        /////////////////////////////////////////////////////////////
        // If the tile is open, then the change in sunlight affects the neighbor tiles.
        // If the tile is closed, then don't propogate changes.
        /////////////////////////////////////////////////////////////

        if (!this.open) {
            return;
        }

        //////////////////////////////////////
        // Apply full sunlight to bottom neighbor
        //////////////////////////////////////

        const neighborBottom = this.getNeighborBottom();
        if (neighborBottom && neighborBottom.lighting.sunlightFull) {
            neighborBottom.handleSunlightStop();
        }
    }

    //////////////////////////////////////////////////
    // Lighting
    //////////////////////////////////////////////////

    markTileAsEverVisible() {
        this.lighting.everVisibleToCharacter = true;

        if (this.mineTileIndex < 0 && !this.open && (this.isCurrency() || this.isPriority())) {
            this.region.mineTiles.push(this);
            this.mineTileIndex = this.region.mineTiles.length - 1;
        }
    }

    markTileAsCurrentlyVisible() {
        this.lighting.markTileAsCurrentlyVisible();

        if (this.mineTileIndex < 0 && !this.open && (this.isCurrency() || this.isPriority())) {
            this.region.mineTiles.push(this);
            this.mineTileIndex = this.region.mineTiles.length - 1;
        }
    }

    markAsSunlightFull() {
        this.lighting.markAsSunlightFull();

        if (this.mineTileIndex < 0 && !this.open && (this.isCurrency() || this.isPriority())) {
            this.region.mineTiles.push(this);
            this.mineTileIndex = this.region.mineTiles.length - 1;
        }
    }

    /**
     * @param {Color} lightColor
     */
    addPositionalLighting(lightColor) {
        this.lighting.addPositionalLighting(lightColor);

        // If the player can see the tile, then the chararacter can too.
        this.markTileAsCurrentlyVisible();
    }

    /**
     * @param {Color} lightColor
     */
    addEnvironmentLighting(lightColor) {
        this.lighting.addEnvironmentLighting(lightColor);
        //this.environmentLighting.add(lightColor);

        // If the player can see the tile, then the chararacter can too.
        this.markTileAsEverVisible();
    }

    /**
     * @param {Color} lightColor
     */
    addDynamicLighting(lightColor) {
        this.lighting.addDynamicLighting(lightColor);

        // If the player can see the tile, then the chararacter can too.
        this.markTileAsEverVisible();
    }

    //////////////////////////////////////////////////
    // Hash
    //////////////////////////////////////////////////

    /**
     * Returns the hash for the grid tile.
     * @returns {number}
     */
    hash() {
        const col = this.origin.x / Settings.tile.size;
        const row = this.origin.y / Settings.tile.size;

        // Simple hash code implementation
        let hash = 17;
        hash = 31 * hash + col;
        hash = 31 * hash + row;
        return hash;        
    }

}